import { Download, Film, History, Loader2, Scissors } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { mediaUrlForPlayback } from '@/lib/mediaUrls';
import { formatMeta } from '@/lib/reelsShortsLayoutEngine';
import { ReelsShortsRenderVersions } from '@/components/reelsShorts/ReelsShortsRenderVersions';

export function ReelsShortsFinalVideoPanel({
  project,
  includeBroll,
  onIncludeBrollChange,
  includeSubtitles,
  onIncludeSubtitlesChange,
  onRender,
  rendering = false,
  disabled = false,
  renderJob = null,
  onRetryRender = null,
  onOpenEditor = null,
  onRecut = null,
  recutting = false,
  onRestoreVersion = null,
  restoringVersionId = null,
}) {
  const url = mediaUrlForPlayback(project?.final_video_url);
  const byFormat = project?.final_videos_by_format && typeof project.final_videos_by_format === 'object'
    ? project.final_videos_by_format
    : {};
  const formatEntries = Object.entries(byFormat).filter(([, row]) => row?.url);
  const versions = Array.isArray(project?.render_versions) ? project.render_versions : [];
  const canRender = Boolean((project?.snippets || []).length) && !disabled;
  const canRecut = Boolean((project?.snippets || []).length) && !disabled && !rendering && !recutting;
  const renderStatus = String(renderJob?.status || '').toLowerCase();
  const renderSuperseded = Boolean(renderJob?.superseded)
    || /superseded by re-cut/i.test(String(renderJob?.error_message || renderJob?.message || ''));
  const renderFailed = renderStatus === 'failed' && !renderSuperseded;
  const renderBusy = ['pending', 'running'].includes(renderStatus);
  const renderAttempt = String(
    renderJob?.render_attempt
      || project?.reels_shorts?.render_attempt
      || '',
  ).toLowerCase();
  const degradedExport = Boolean(url) && renderAttempt && renderAttempt !== 'full';
  const disabledReason = disabled
    ? 'Wait until language change finishes, or upload a clip first.'
    : rendering
      ? 'Starting the render job…'
      : '';

  return (
    <div className="rounded-xl border border-[#1f2635] bg-[#12151d] p-4 space-y-3" data-testid="reels-shorts-final-video">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-sm font-semibold text-white flex items-center gap-2">
            <Film className="w-4 h-4 text-rose-300" />
            Final reel
          </h3>
          <p className="text-[12px] text-slate-500 mt-1">
            <strong className="text-slate-400 font-medium">Re-export active format</strong> encodes
            only the selected canvas (e.g. 9:16).{' '}
            <strong className="text-slate-400 font-medium">Generate all formats</strong> in the
            layout engine encodes every checked aspect ratio — they are not the same action.
            Arrange the preview first, then export.
          </p>
        </div>
        <div className="flex flex-col items-stretch sm:items-end gap-2">
          {onOpenEditor ? (
            <Button
              type="button"
              size="sm"
              onClick={onOpenEditor}
              className="bg-rose-500 hover:bg-rose-400 text-black relative z-10"
              data-testid="reels-shorts-open-editor"
            >
              <Scissors className="w-3.5 h-3.5 mr-1.5" />
              Arrange layout
            </Button>
          ) : null}
          {onRecut ? (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={onRecut}
              disabled={!canRecut || renderBusy}
              title="Re-run the AI editor on the same clips and export again. Prior MP4s stay in history."
              className="relative z-10"
              data-testid="reels-shorts-recut"
            >
              {recutting ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
              ) : (
                <History className="w-3.5 h-3.5 mr-1.5" />
              )}
              {url || versions.length ? 'Re-cut polished reel' : 'Cut polished reel'}
            </Button>
          ) : null}
          {renderFailed && onRetryRender ? (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={onRetryRender}
            disabled={rendering || recutting}
            data-testid="reels-shorts-retry-render"
          >
            {rendering ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
            ) : (
              <Film className="w-3.5 h-3.5 mr-1.5" />
            )}
            Retry final video
          </Button>
        ) : (
          <Button
            type="button"
            size="sm"
            onClick={onRender}
            disabled={!canRender || rendering || recutting}
            title={disabledReason || undefined}
            className="bg-transparent border border-[#2a3344] text-slate-200 hover:bg-[#1a202c] relative z-10"
            data-testid="reels-shorts-render-final"
          >
            {rendering ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
            ) : (
              <Film className="w-3.5 h-3.5 mr-1.5" />
            )}
            {url ? 'Re-export active format' : 'Export active format'}
          </Button>
          )}
          <p className="text-[10px] text-slate-500 max-w-[220px] text-right sm:text-left">
            Active format only. For every checked ratio use Generate all formats in Multi-format canvas.
          </p>
        </div>
      </div>

      <label className="flex items-center gap-2 text-[12px] text-slate-300 cursor-pointer">
        <input
          type="checkbox"
          checked={includeBroll}
          onChange={(event) => onIncludeBrollChange(event.target.checked)}
          disabled={rendering}
          className="rounded border-slate-600"
        />
        Include B-ROLL (vertical/horizontal split, bands, overlays, cards)
      </label>
      <label className="flex items-center gap-2 text-[12px] text-slate-300 cursor-pointer">
        <input
          type="checkbox"
          checked={includeSubtitles}
          onChange={(event) => onIncludeSubtitlesChange(event.target.checked)}
          disabled={rendering}
          className="rounded border-slate-600"
        />
        Include subtitles (large spoken words only, kept off B-ROLL)
      </label>

      {renderBusy && !renderFailed ? (
        <p className="text-[12px] text-rose-200" data-testid="reels-shorts-render-progress">
          {renderJob?.video_percent != null
            ? `${Math.round(Number(renderJob.video_percent))}% video completed`
            : (renderJob?.message || 'Rendering final video…')}
        </p>
      ) : null}

      {renderFailed && renderJob?.error_message ? (
        <p className="text-[12px] text-rose-300" data-testid="reels-shorts-render-error">
          {renderJob.error_message}
        </p>
      ) : null}

      {degradedExport ? (
        <p
          className="text-[12px] text-amber-200/90 rounded-lg border border-amber-400/30 bg-amber-400/10 px-3 py-2"
          data-testid="reels-shorts-render-degraded"
        >
          Last export used a {renderAttempt} fallback — B-ROLL, multi-screen layouts, and/or
          subtitles may be missing. Keep both checkboxes on and re-export.
          {renderJob?.message ? ` (${renderJob.message})` : ''}
        </p>
      ) : null}

      {url ? (
        <div className="space-y-2">
          <video
            key={url}
            src={url}
            controls
            playsInline
            className="w-full max-w-[280px] mx-auto rounded-xl bg-black aspect-[9/16] object-cover"
            data-testid="reels-shorts-final-player"
          />
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-[12px] text-rose-200 hover:text-rose-100"
          >
            <Download className="w-3.5 h-3.5" />
            Open / download MP4
          </a>
        </div>
      ) : (
        <p className="text-[12px] text-slate-500">
          Run the AI editor first for a smarter order, then generate. Upload order is used if you skip the editor.
        </p>
      )}

      {formatEntries.length > 1 ? (
        <div className="space-y-1.5 pt-1" data-testid="reels-shorts-format-downloads">
          <p className="text-[10px] uppercase tracking-wide text-slate-500">All formats</p>
          <div className="flex flex-wrap gap-1.5">
            {formatEntries.map(([fmt, row]) => {
              const href = mediaUrlForPlayback(row.url);
              if (!href) return null;
              const qa = row.qa || project?.final_qa?.[fmt];
              const qaFail = qa && qa.passed === false;
              return (
                <a
                  key={fmt}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-black/30 px-2.5 py-1 text-[10px] text-slate-200 hover:border-rose-400/40"
                  data-testid={`reels-shorts-download-${fmt.replace(':', '-')}`}
                  title={qaFail ? `QA: ${qa.reason || 'collision'}` : qa?.replan_applied ? 'QA replan applied' : 'QA passed'}
                >
                  <Download className="w-3 h-3" />
                  {fmt} · {formatMeta(fmt).shortLabel}
                  {qa?.replan_applied ? ' · replanned' : qaFail ? ' · QA' : ''}
                </a>
              );
            })}
          </div>
        </div>
      ) : null}

      {versions.length ? (
        <ReelsShortsRenderVersions
          versions={versions}
          onRestore={onRestoreVersion}
          restoringId={restoringVersionId}
          disabled={disabled || rendering || recutting || renderBusy}
        />
      ) : null}
    </div>
  );
}

export default ReelsShortsFinalVideoPanel;
